// Balance Scale: find the weight of the mystery box. The beam tips by the real weight
// difference, so she can try a value and see which way it tips before locking in.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { makeBalance, tilt, sideWeight, type BalanceProblem, type Side } from '@problems/balance';
import { makeNumpad, makeChoices } from '@games/common/numpad';

const PROBLEMS = 6;
const BLOCK_COLORS = ['#4f8fe6', '#3bb3a9', '#f08a3c', '#5fbf6a', '#e2574c'];
const MYSTERY = '#9b6be6';

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let level = ctx.currentLevel();
  let p: BalanceProblem = makeBalance(level, ctx.rng);
  let trial: number | null = null;
  let angle = 0, targetAngle = 0;
  let wrongs = 0, hinted = false, locked = false;
  let attempts = 0, correct = 0, minLevel = level;
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Scale ', h('strong', null, '1'), ` of ${PROBLEMS}`), h('span.muted.small', null, ''));
  const stage = h('div');
  const eq = h('div.center', { style: { fontWeight: '800', fontSize: '22px', minHeight: '30px' } });
  const words = h('p.center.muted.small', { style: { minHeight: '20px' } });
  const controls = h('div');
  const lockBtn = h('button.btn.primary.block', { onclick: lockIn }, 'Lock in') as HTMLButtonElement;
  host.append(head, stage, eq, words, controls, lockBtn);

  const S = makeSurface(stage, { aspect: 0.62, draw: false });
  const loop = makeLoop(() => { angle += (targetAngle - angle) * 0.12; }, draw);
  loop.start();

  function setup() {
    level = ctx.currentLevel();
    minLevel = Math.min(minLevel, level);
    p = makeBalance(level, ctx.rng);
    trial = null; wrongs = 0; hinted = false; locked = false;
    targetAngle = 0;
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    (head.lastElementChild as HTMLElement).textContent = p.label === '?' ? 'How heavy is the box?' : 'Find x';
    eq.textContent = p.symbols;
    words.textContent = p.words;
    lockBtn.disabled = true;
    renderControls();
  }

  function renderControls() {
    clear(controls);
    if (p.choices) {
      const ch = makeChoices(p.choices, (v) => { tryValue(Number(v)); });
      controls.append(h('p.center.muted.small', null, 'Tap a weight to try it on the scale.'), ch.el);
    } else {
      const pad = makeNumpad({ onEnter: (v) => tryValue(v), enterLabel: 'Try it' });
      controls.append(pad.el);
    }
  }

  function tryValue(v: number) {
    if (locked) return;
    if (!Number.isInteger(v) || v < 0 || v > 999) { ctx.say('Try a whole number.'); return; }
    trial = v;
    const d = tilt(p, v);
    targetAngle = Math.max(-0.32, Math.min(0.32, d * 0.04));
    lockBtn.disabled = false;
    ctx.sound('pop');
    if (d === 0) ctx.say(`With ${p.label} = ${v} the scale balances. Lock it in!`);
    else ctx.say(`With ${p.label} = ${v} the ${d > 0 ? 'right' : 'left'} side is heavier.`);
  }

  function lockIn() {
    if (locked || trial === null) return;
    const ms = performance.now() - tProblem;
    attempts++;
    if (trial === p.answer) {
      correct++; locked = true;
      ctx.report({ skill: 'algebra', level, correct: true, hintUsed: hinted, ms, problemId: `balance:${p.symbols}` });
      ctx.sound('ding');
      ctx.say(`Yes! ${p.label} = ${p.answer}. Both sides weigh ${sideWeight(p.left, p.answer)}.`);
      next();
    } else {
      wrongs++;
      ctx.report({ skill: 'algebra', level, correct: false, hintUsed: hinted, ms, problemId: `balance:${p.symbols}` });
      ctx.sound('buzz');
      const heavy = tilt(p, trial) > 0 ? 'right' : 'left';
      const mysterySide = p.right.x > p.left.x ? 'right' : 'left';
      const direction = heavy === mysterySide ? 'smaller' : 'bigger';
      hinted = true;
      if (wrongs >= 2) {
        locked = true;
        ctx.say(`The answer was ${p.label} = ${p.answer}. Watch it balance.`);
        trial = p.answer; targetAngle = 0;
        next(1600);
      } else {
        ctx.say(`Not yet. The ${heavy} side is heavier, so ${p.label} must be ${direction} than ${trial}.`);
      }
    }
  }

  function next(delay = 1000) {
    n++;
    setTimeout(() => {
      if (n >= PROBLEMS) {
        if (attempts === correct && minLevel >= 5) ctx.charm('charm.balance');
        ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 });
      } else setup();
    }, delay);
  }

  function drawSide(c: CanvasRenderingContext2D, s: Side, cx: number, cy: number, unit: number, showTrial: number | null) {
    // pan
    c.fillStyle = '#8a5a3b'; roundRect(c, cx - unit * 2.2, cy, unit * 4.4, unit * 0.35, 4); c.fill();
    const items: { label: string; mystery: boolean }[] = [];
    for (let i = 0; i < s.x; i++) items.push({ label: showTrial !== null ? String(showTrial) : p.label, mystery: true });
    for (const k of s.known) items.push({ label: String(k), mystery: false });
    const bw = unit * 1.1, gap = unit * 0.12;
    const perRow = Math.max(1, Math.floor((unit * 4.4) / (bw + gap)));
    items.forEach((it, i) => {
      const row = Math.floor(i / perRow), col = i % perRow;
      const rowCount = Math.min(perRow, items.length - row * perRow);
      const x0 = cx - (rowCount * (bw + gap) - gap) / 2;
      const x = x0 + col * (bw + gap), y = cy - (row + 1) * (bw + gap) + gap;
      c.fillStyle = it.mystery ? MYSTERY : BLOCK_COLORS[(Number(it.label) || i) % BLOCK_COLORS.length];
      roundRect(c, x, y, bw, bw, 4); c.fill();
      c.fillStyle = '#fff'; c.font = `900 ${Math.round(bw * (it.label.length > 2 ? 0.36 : 0.5))}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText(it.label, x + bw / 2, y + bw / 2 + 1);
    });
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = '#efe9db'; c.fillRect(0, 0, W, H);
    const unit = Math.min(W, H) * 0.09;
    const px = W / 2, py = H * 0.32, half = W * 0.36;
    // stand
    c.fillStyle = '#6d6b67'; c.beginPath(); c.moveTo(px - unit * 1.4, H * 0.95); c.lineTo(px + unit * 1.4, H * 0.95); c.lineTo(px + unit * 0.25, py); c.lineTo(px - unit * 0.25, py); c.closePath(); c.fill();
    // beam
    const lx = px - Math.cos(angle) * half, ly = py - Math.sin(angle) * half;
    const rx = px + Math.cos(angle) * half, ry = py + Math.sin(angle) * half;
    c.strokeStyle = '#2a2a2e'; c.lineWidth = Math.max(4, unit * 0.3); c.lineCap = 'round';
    c.beginPath(); c.moveTo(lx, ly); c.lineTo(rx, ry); c.stroke();
    c.fillStyle = '#f5c542'; c.beginPath(); c.arc(px, py, unit * 0.35, 0, 7); c.fill();
    // strings and pans
    const drop = H * 0.34;
    c.strokeStyle = '#2a2a2e'; c.lineWidth = 2;
    c.beginPath(); c.moveTo(lx, ly); c.lineTo(lx, ly + drop); c.moveTo(rx, ry); c.lineTo(rx, ry + drop); c.stroke();
    drawSide(c, p.left, lx, ly + drop, unit, trial);
    drawSide(c, p.right, rx, ry + drop, unit, trial);
    // status
    const d = trial === null ? 0 : tilt(p, trial);
    c.fillStyle = '#2a2a2e'; c.font = `800 ${Math.round(unit * 0.45)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'alphabetic';
    c.fillText(trial === null ? 'Balanced. What is inside the box?' : d === 0 ? 'Balanced!' : d > 0 ? 'Right side is heavier' : 'Left side is heavier', W / 2, H * 0.12);
  }

  setup();
  return { destroy() { loop.stop(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.balance-scale',
  title: 'Balance Scale',
  blurb: 'A mystery box sits on a real balance. Try a weight, watch it tip, find x.',
  category: 'math',
  skills: ['algebra'],
  levelRange: [1, 10],
  unlock: { stars: 7 },
  mount,
};
export default mod;
